import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { BsCheckBox, BsSquare } from 'react-icons/bs'
import { loadStripe } from '@stripe/stripe-js'

import Alert from '../common/Alert'
import { APIURL } from '../../constants'
import LoadingButton from '../common/LoadingButton'
import { AdminButton } from '/components/theme/admin/AdminButton'
import BillingFrequencySwitch from './BillingFrequencySwitch'
import CouponCode from './CouponCode'
import { currencyN } from '../utils'
import services from '../services'

const calcBilling = (cost, coupon) => {
  const { valid, amount_off, percent_off } = coupon
  const subtotal = cost
  let total = subtotal
  let discount = 0
  if (valid) {
    if (percent_off) {
      discount = subtotal * (percent_off * 0.01)
    } else if (amount_off) {
      discount = amount_off * 0.01
    }
    total -= discount
  }
  return {
    subtotal,
    total,
    discount
  }
}

const getProratedAmount = async (stripe_price_id, isEnterprise) => {
  if (stripe_price_id) {
    const response = await services.get_v2(APIURL.API_RETREIVE_PRORATE_COST, {
      params: {
        stripe_price_id,
        type: isEnterprise ? 'enterprise' : 'user'
      }
    })
    return response
  } else {
    return null
  }
}

const ELEMENTS_OPTS = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css?family=Montserrat:300',
      weight: '300'
    }
  ]
}

const CARD_IMAGES = {
  Visa: 'visa-card.png',
  MasterCard: 'master-card.png',
  Discover: 'discover-card.png',
  'Diners Club': 'discover-card.png',
  JCB: 'discover-card.png',
  'American Express': 'american-express-card.png'
}

const makeCardImage = (brand) => {
  return '/assets/images/card-images/' + CARD_IMAGES[brand]
}

const CardList = ({ isUpgrade, isEnterprise, setStripeCardId }) => {
  const [isNewCard, setIsNewCard] = useState(!isUpgrade)
  const [noCards, setNoCards] = useState(true)
  const [userCardList, setUserCardList] = useState([])

  const changeCard = async (stripe_card_id) => {
    const params = {
      stripe_card_id: stripe_card_id
    }
    await services.post_v2(APIURL.API_STRIPE_SET_DETAULT_CARD, params)
    const cards_response = await services.get_v2(APIURL.API_STRIPE_GET_CARD, {
      params: {
        type: isEnterprise ? 'enterprise' : 'user'
      }
    })
    const cards = cards_response.response
    cards.sort((a, b) => (a.id > b.id ? 1 : -1))
    setUserCardList(cards)
  }

  const toggleUseExisting = (use_new) => {
    setIsNewCard((prev) => !prev)
    if (use_new) {
      setStripeCardId('')
    }
  }

  useEffect(() => {
    ;(async () => {
      const cardsRes = await services.get_v2(APIURL.API_STRIPE_GET_CARD, {
        params: {
          type: isEnterprise ? 'enterprise' : 'user'
        }
      })
      const cards = cardsRes.response
      if (cards) {
        cards.sort((a, b) => (a.id > b.id ? 1 : -1))
        setUserCardList(cards)
        setNoCards(cards.length === 0)
      } else {
        setUserCardList([])
        setNoCards(true)
      }
    })()
  }, [])

  let list = ''
  if (userCardList.length > 0) {
    list = userCardList.map((card) => {
      let classes = 'cards-display p-xs clickable'
      if (card.default_card) {
        classes += ' card-active'
        setStripeCardId(card.stripe_card_id)
      }
      return (
        <div
          className={classes}
          key={card.id}
          onClick={() => {
            changeCard(card.stripe_card_id)
          }}
        >
          <div className="card-info">
            <img
              className="card-item"
              src={makeCardImage(card.brand)}
              data-brand={card.brand}
              alt={card.brand}
            />
            <div className="ml-md">
              {card.funding} ending in {card.last4}
            </div>
          </div>
          {parseInt(card.default_card) ? (
            <BsCheckBox className="" />
          ) : (
            <BsSquare className="clickable" />
          )}
        </div>
      )
    })
  }
  let cardElement = (
    <div className="stripe-control mb-lg">
      <CardElement
        options={{
          style: {
            base: {
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 300,
              '::placeholder': {
                color: '#999',
                fontWeight: 300
              }
            }
          }
        }}
      />
    </div>
  )
  if (noCards) {
    return cardElement
  }
  return (
    <div className={noCards && 'display-none'}>
      <div className="pl-xs pb-md">
        Payment Method
        {!isNewCard && (
          <a>
            <small
              onClick={() => toggleUseExisting(true)}
              className="pull-right"
            >
              Use Different Card
            </small>
          </a>
        )}
        {isNewCard && (
          <a>
            <small
              onClick={() => toggleUseExisting(false)}
              className="pull-right"
            >
              Use Existing Card
            </small>
          </a>
        )}
      </div>
      <div key="card-content">
        {isNewCard && cardElement}
        {!isNewCard && list}
      </div>
    </div>
  )
}

const PaymentForm = inject('store')(
  observer(
    ({
      store,
      onSuccess,
      plans,
      plan,
      price,
      coupon,
      setPlan,
      setPrice,
      isUpgrade = false,
      isEnterprise = false,
      includeHeader = true
    }) => {
      const [loading, setLoading] = useState()
      const [stripeCardId, setStripeCardId] = useState()
      const [couponData, setCouponData] = useState({})
      const [error, setError] = useState('')
      const [proratedCost, setProratedCost] = useState(0)

      const isFreePlan = plan.pricing_model === 'free'
      let billing = { subtotal: 0, discount: 0, total: 0 }
      if (!isFreePlan) {
        billing = calcBilling(
          price.interval === 'monthly'
            ? Number(price.amount / 100)
            : Number(price.amount / 100),
          couponData
        )
      }
      let proratedAmount = 0
      const { subtotal, discount } = billing
      let { total } = billing

      let trialPrice = total
      if (plan.trial_duration > 0) {
        total = 0
      }

      let credit_message = ''
      if (total < 0) {
        credit_message =
          '*There will be a ' +
          currencyN(total * -1) +
          ' credit on your account after this purchase.'
        total = 0
      }

      const stripe = useStripe()
      const elements = useElements()

      const freeSubscribe = async () => {
        let res = null
        try {
          res = await store.processFreePlan({
            plan_id: plan.id
          })
          if (res.status === 'success') {
            onSuccess()
          } else {
            setLoading(false)
            setError(res.message)
          }
        } catch (e) {
          setLoading(false)
          if (res) {
            setError(res.message)
          }
        }
      }

      const paymentSubscribe = async () => {
        try {
          let params = {}
          if (!stripeCardId) {
            const { token } = await stripe.createToken(
              elements.getElement(CardElement),
              {
                name: store?.sessionData?.userData?.email
              }
            )
            if (!token) {
              setLoading(false)
              return
            }

            params = {
              stripeToken: token.id,
              stripe_card_id: stripeCardId,
              coupon: couponData?.coupon,
              stripe_plan_id: price.stripe_price_id,
              type: isEnterprise ? 'enterprise' : 'member'
            }
          } else {
            params = {
              stripeToken: '',
              stripe_card_id: stripeCardId,
              coupon: couponData?.coupon,
              stripe_plan_id: price.stripe_price_id,
              type: isEnterprise ? 'enterprise' : 'member'
            }
          }

          const res = await store.processPayment(params)
          if (res.status === 'success') {
            onSuccess()
          } else {
            setError(res.message)
            setLoading(false)
          }
        } catch (e) {
          setLoading(false)
          setError(e.message)
        }
      }

      const handleSubmit = async () => {
        setLoading(true)
        if (isFreePlan) {
          await freeSubscribe()
        } else {
          await paymentSubscribe()
        }
      }

      useEffect(() => {
        ;(async () => {
          if (!isFreePlan && price) {
            const res = await getProratedAmount(
              price.stripe_price_id,
              isEnterprise
            )
            if (res && res.status === 'success') {
              setProratedCost(res.response.cost / 100)
            }
          }
        })()
      }, [proratedCost, setProratedCost, price, isFreePlan, isEnterprise])

      if (proratedCost) {
        proratedAmount = Math.max(total - Math.abs(proratedCost), 0)
        total = Math.abs(proratedCost)
      }
      return (
        <Formik
          initialValues={{}}
          onSubmit={async (values) => {
            await handleSubmit(values)
          }}
        >
          {({ isValid }) => (
            <Form className="registration-container">
              {error && <Alert type="danger" message={error} />}
              {includeHeader && (
                <div className="row">
                  <div className="col-md-12">
                    <h2 className="secondary" style={{ textAlign: 'center' }}>
                      Payment Information
                    </h2>
                  </div>
                </div>
              )}
              <div style={{ margin: '5px 0' }}>
                <BillingFrequencySwitch
                  plans={plans}
                  plan={plan}
                  price={price}
                  setPlan={setPlan}
                  setPrice={setPrice}
                />
              </div>
              {!isFreePlan && (
                <CouponCode
                  coupon_code={coupon}
                  planId={plan.id}
                  priceId={price.stripe_price_id}
                  couponData={couponData}
                  onChange={(value) => {
                    setCouponData(value)
                  }}
                />
              )}
              {!isFreePlan && (
                <CardList
                  isUpgrade={isUpgrade}
                  isEnterprise={isEnterprise}
                  setStripeCardId={setStripeCardId}
                />
              )}
              <div className="row billing-table">
                <div className="col-sm-12">
                  {discount > 0 && (
                    <div>
                      <div className="row">
                        <div className="col-sm-6">Subtotal</div>
                        <div className="col-sm-6">{currencyN(subtotal)}</div>
                      </div>
                      <div className="row">
                        <div className="col-sm-6">Discount</div>
                        <div className="col-sm-6">
                          {currencyN(discount * -1)}
                        </div>
                      </div>
                      {proratedAmount > 0 && (
                        <div className="row credit-text">
                          <div className="col-sm-6">Prorated Amount</div>
                          <div className="col-sm-6">
                            -{currencyN(proratedAmount)}
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col-sm-6">
                          <strong>Today&#39;s Total</strong>
                        </div>
                        <div className="col-sm-6">
                          <strong>{currencyN(total)}</strong>
                        </div>
                      </div>
                      {credit_message && (
                        <div className="row credit-text">
                          <div className="col-sm-12">{credit_message}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {discount === 0 && (
                    <div>
                      {proratedAmount > 0 && (
                        <div>
                          <div className="row">
                            <div className="col-sm-6">Subtotal</div>
                            <div className="col-sm-6">
                              {currencyN(subtotal)}
                            </div>
                          </div>
                          <div className="row credit-text">
                            <div className="col-sm-6">Prorated Amount</div>
                            <div className="col-sm-6">
                              -{currencyN(proratedAmount)}
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col-sm-6">
                          <strong>
                            Today&#39;s Total
                          </strong>
                        </div>
                        <div className="col-sm-6">
                          <strong>{currencyN(total)}</strong>
                        </div>
                      </div>
                      {plan.trial_duration && (
                        <div className="row">
                          <div className="col-md-12">
                            {currencyN(trialPrice)} will be charged in {plan.trial_duration} days after the trial period ends.
                          </div>
                        </div>
                      )}
                      {credit_message && (
                        <div className="row">
                          <div className="col-sm-12">{credit_message}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {isEnterprise && (
                <AdminButton disable={!isValid} isLoading={loading}>
                  {isUpgrade ? 'Upgrade' : 'Become a Member'}
                </AdminButton>
              )}
              {!isEnterprise && (
                <LoadingButton
                  text={isUpgrade ? 'Upgrade' : 'Become a Member'}
                  className="action-btn-gld ml-none"
                  disable={!isValid}
                  isLoading={loading}
                />
              )}
            </Form>
          )}
        </Formik>
      )
    }
  )
)

const PaymentFormWrapper = inject('store')(
  observer((props) => {
    return (
      <Elements
        stripe={loadStripe(props.store.siteData.get('stripe_pub_key'))}
        options={ELEMENTS_OPTS}
      >
        <PaymentForm {...props} />
      </Elements>
    )
  })
)

export default PaymentFormWrapper
